import { useAnalytics } from "../../context/AnalyticsContext";
import { useDioptraFetch } from "../../hooks/use-dioptra-fetch";
import { getEnv } from "../../utils/envManager";
import { toastError } from "../../utils/toast";
import { chatResponseCopyText } from "@common/utils/chatResponseText";
import { copyRichText } from "@common/utils/copyRichText";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseMessageFeedbackInputs {
  conversationId: string;
}

export function useMessageFeedback({ conversationId }: UseMessageFeedbackInputs) {
  const { dioptraFetch } = useDioptraFetch();
  const { sendError, analytics } = useAnalytics();
  const [copyClipboardIndicator, setCopyClipboardIndicator] = useState<number | undefined>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showFeedbackFormForMessageId, setShowFeedbackFormForMessageId] = useState<string | null>(null);
  const [savePromptIndicator, setSavePromptIndicator] = useState<number | undefined>();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (copyClipboardIndicator) {
      timeoutRef.current = setTimeout(() => setCopyClipboardIndicator(undefined), 1500);
    }
    return () => {
      clearTimeout(timeoutRef.current);
    }
  }, [copyClipboardIndicator]);
  useEffect(() => {
    if (savePromptIndicator !== undefined) {
      saveTimeoutRef.current = setTimeout(() => setSavePromptIndicator(undefined), 1500);
    }
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [savePromptIndicator]);
  const handleSaveUserMessageAsPrompt = useCallback(async (messageContent: string, index: number) => {
    try {
      const name = (messageContent || '').slice(0, 50);
      await dioptraFetch(`${getEnv("WEBAPP_API_BASE_URL")}/api/prompts/user`, {
        method: "POST",
        body: JSON.stringify({ name, content: messageContent, type: "chat" })
      });
      setSavePromptIndicator(index);
    } catch (error) {
      toastError(error);
      sendError(error instanceof Error ? error : new Error(String(error)), {
        component: 'ChatPage',
        action: 'save_user_prompt'
      });
    }
  }, [dioptraFetch, sendError]);
  const handleCopyToClipboard = useCallback(async (content: string, index: number) => {
    const success = await copyRichText(chatResponseCopyText(content));
    if (!success) {
      toastError("Could not copy the response to the clipboard.");
      sendError(new Error("Clipboard copy failed on every available method"), {
        component: "ChatPage",
        action: "copy_message_to_clipboard",
        conversationId,
      });
      return;
    }
    analytics?.track("chat_response_copied", { conversationId });
    setCopyClipboardIndicator(index);
  }, [analytics, sendError, conversationId]);
  return { copyClipboardIndicator, savePromptIndicator, showFeedbackFormForMessageId, setShowFeedbackFormForMessageId, handleSaveUserMessageAsPrompt, handleCopyToClipboard };
}
